import React from 'react'
import { Box, Typography } from '@mui/material'

import { INTERAX_COLORS } from '../../../../constants/colors'

export type InvitePreviewUser = {
  id: string
  name: string
  email: string
}

interface InviteUsersPreviewListProps {
  users: InvitePreviewUser[]
  title: string
  nameFallback: string
  emailFallback: string
  removeLabel: string
  activeIndex: number | null
  onSelect: (index: number) => void
  onRemove: (index: number) => void
}

export const InviteUsersPreviewList: React.FC<InviteUsersPreviewListProps> = ({
  users,
  title,
  nameFallback,
  emailFallback,
  removeLabel,
  activeIndex,
  onSelect,
  onRemove
}) => {
  const hasEntries = users.some((user) => user.name || user.email)

  return (
    <Box
      sx={{
        flex: '1 1 45%',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
        borderRadius: '16px',
        border: `1px solid ${INTERAX_COLORS.heritageBlue}`,
        backgroundColor: 'rgba(7, 13, 20, 0.75)',
        minWidth: 0,
        minHeight: 0
      }}
    >
      <Typography sx={{ color: INTERAX_COLORS.white, fontWeight: 600, fontSize: '18px' }}>{title}</Typography>

      {hasEntries ? (
        <Box
          component="ul"
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}
        >
          {users.map((user, index) => {
            const isActive = index === activeIndex
            return (
              <Box
                component="li"
                key={user.id}
                onClick={() => onSelect(index)}
                sx={{
                  position: 'relative',
                  listStyle: 'none',
                  padding: '12px 48px 12px 14px',
                  borderRadius: '12px',
                  border: `1px solid ${isActive ? INTERAX_COLORS.sun : INTERAX_COLORS.heritageBlue40}`,
                  backgroundColor: isActive ? 'rgba(255, 192, 0, 0.12)' : 'rgba(255, 255, 255, 0.03)',
                  cursor: 'pointer'
                }}
              >
                <Typography sx={{ color: INTERAX_COLORS.white, fontWeight: 600, fontSize: '14px' }}>
                  {index + 1}. {user.name || nameFallback}
                </Typography>
                <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: '12px' }}>
                  {user.email || emailFallback}
                </Typography>

                <Box
                  component="button"
                  type="button"
                  aria-label={removeLabel}
                  onClick={(event?: React.MouseEvent<HTMLButtonElement>) => {
                    event?.stopPropagation()
                    onRemove(index)
                  }}
                  sx={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    width: '26px',
                    height: '26px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    color: INTERAX_COLORS.thunderGrey,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease, color 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(231, 76, 60, 0.2)',
                      color: INTERAX_COLORS.redError
                    }
                  }}
                >
                  ✕
                </Box>
              </Box>
            )
          })}
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: '120px',
            borderRadius: '12px',
            border: `1px dashed ${INTERAX_COLORS.heritageBlue40}`,
            backgroundColor: 'rgba(255, 255, 255, 0.02)'
          }}
          aria-hidden
        />
      )}
    </Box>
  )
}
